import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';
import { CookieService } from '../services/cookie.service';

/**
 * Applies required headers for auth and checks responses for auth problems to redirect to login etc.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private auth: AuthService,
    private cookie: CookieService,
    private logger: NGXLogger
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let newReq = request.clone();

    // Add auth stuff to HTTP header for requests to Salfeld API (except on login/logout)
    if (request.url.startsWith(environment.apiUrl) && this.auth.isLoggedIn && !this.isLoginLogoutRequest(request.url)) {
      let headers = request.headers;

      // If the pcid header was set elsewhere, we do not overwrite it here
      if (request.headers.get('X-pcid') == null) {
        // Attach id of active device if there is an active device
        // WORKAROUND for (deviceService.activeDevice.device.id):
        // Using cookie instead of accessing DeviceService to get id of current device because of cyclic dependency error.
        // Explanation:
        // If the page is reloaded, AuthService instantly performs login using the already available session cookie (without API request).
        // Because the user is already logged when DeviceService is created, it instantly requests available devices from API.
        // This API request leads to the call of this interceptor. The call is most likely made before this interceptor was created.
        // Because of this, this interceptor is created and requires DeviceService to be injected. But the service was created just before
        // which triggers the cyclic dependency error.
        // If we waited some time before DeviceService initiates an API request, everything would be fine...
        // Edit Markus 2021-03-23: The above problem is fixed now but we leave this as it is for now
        const pcId = this.cookie.getCookie('pcId');
        if (pcId) {
          headers = headers.set('X-pcid', pcId);
        }
      }

      // Add sessionId
      headers = headers.set('X-sessionId', this.auth.sessionId);

      newReq = request.clone({
        headers
      });
    }

    // Prevent cancelling login/logout requests and also prevent that we handle 401 for login/logout because it could be a wrong password
    if (!this.isLoginLogoutRequest(newReq.url)) {
      return next.handle(newReq).pipe(
        // Cancel all requests if user is logged out to prevent 401 errors
        takeUntil(this.auth.onLogout),
        catchError((error: HttpErrorResponse) => {
          if (!(error.error instanceof ErrorEvent) && error.status === 401) {
            this.logger.info('Got 401 response, logging out');
            this.auth.logout();
          }
  
          return throwError(error);
        })
      );
    } else {
      return next.handle(newReq);
    }
  }

  private isLoginLogoutRequest(url: string): boolean {
    return url.startsWith(environment.apiUrl + 'login');
  }
}
